import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  practitionerId: string;
  practitionerName: string;
  clientId: string;
}

interface ExistingReview {
  id: number;
  rating: number;
  comment: string | null;
  anonymous: boolean;
  created_at: string;
}

export const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  bookingId,
  practitionerId,
  practitionerName,
  clientId,
}) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingReview, setExistingReview] = useState<ExistingReview | null>(null);
  const [isLoadingReview, setIsLoadingReview] = useState(true);
  const { toast } = useToast();

  // Check if a review already exists for this booking
  useEffect(() => {
    const checkExistingReview = async () => {
      if (!isOpen) return;

      setIsLoadingReview(true);
      try {
        const { data, error } = await supabase
          .from('reviews')
          .select('id, rating, comment, anonymous, created_at')
          .eq('booking_id', bookingId)
          .eq('client_id', clientId)
          .maybeSingle();

        if (error) {
          console.error('Error checking for existing review:', error);
        } else if (data) {
          setExistingReview(data);
        }
      } catch (error) {
        console.error('Error checking for existing review:', error);
      } finally {
        setIsLoadingReview(false);
      }
    };

    checkExistingReview();
  }, [isOpen, bookingId, clientId]);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({
        title: 'Rating Required',
        description: 'Please select a rating before submitting your review.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('reviews')
        .insert({
          practitioner_id: practitionerId,
          client_id: clientId,
          booking_id: bookingId,
          rating,
          comment: comment.trim() || null,
          anonymous,
          is_published: true,
        });

      if (error) throw error;

      toast({
        title: 'Review Submitted',
        description: 'Thank you for your feedback!',
      });

      // Reset form and close
      setRating(0);
      setComment('');
      setAnonymous(false);
      onClose();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: 'Submission Failed',
        description: 'Failed to submit your review. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {existingReview ? 'Your Review' : 'Review Your Session'}
          </DialogTitle>
          <DialogDescription>
            {existingReview
              ? `Your review for ${practitionerName}`
              : `Share your experience with ${practitionerName}`
            }
          </DialogDescription>
        </DialogHeader>

        {isLoadingReview ? (
          <div className="py-8 text-center text-gray-500">
            Loading...
          </div>
        ) : existingReview ? (
          // Show existing review
          <div className="space-y-6 py-4">
            {/* Rating Stars - Read Only */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Rating</label>
              <div className="flex items-center space-x-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`h-8 w-8 ${
                      star <= existingReview.rating
                        ? 'text-amber-500 fill-amber-500'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
                <span className="ml-2 text-sm text-gray-600">
                  {existingReview.rating} star{existingReview.rating !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {/* Comment - Read Only */}
            {existingReview.comment && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Your Comment</label>
                <div className="rounded-md border bg-gray-50 p-3 text-sm">
                  {existingReview.comment}
                </div>
              </div>
            )}

            {/* Anonymous Badge */}
            {existingReview.anonymous && (
              <div className="text-xs text-gray-500">
                Posted anonymously
              </div>
            )}

            {/* Submitted Date */}
            <div className="text-xs text-gray-500">
              Submitted on {new Date(existingReview.created_at).toLocaleDateString()}
            </div>
          </div>
        ) : (
          // Show review form
          <div className="space-y-6 py-4">
            {/* Rating Stars */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Rating *</label>
              <div className="flex items-center space-x-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="focus:outline-none transition-transform hover:scale-110"
                  >
                    <Star
                      className={`h-8 w-8 ${
                        star <= (hoveredRating || rating)
                          ? 'text-amber-500 fill-amber-500'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
                <span className="ml-2 text-sm text-gray-600">
                  {rating > 0 ? `${rating} star${rating !== 1 ? 's' : ''}` : 'Select a rating'}
                </span>
              </div>
            </div>

            {/* Comment */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Comment (optional)</label>
              <Textarea
                placeholder="Share your experience..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                maxLength={1000}
              />
              <p className="text-xs text-gray-500">{comment.length}/1000 characters</p>
            </div>

            {/* Anonymous Option */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="anonymous"
                checked={anonymous}
                onCheckedChange={(checked) => setAnonymous(checked as boolean)}
              />
              <label
                htmlFor="anonymous"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Post anonymously
              </label>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {existingReview ? 'Close' : 'Cancel'}
          </Button>
          {!existingReview && (
            <Button onClick={handleSubmit} disabled={isSubmitting || rating === 0}>
              {isSubmitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
